import { useState, useEffect } from "react";
import {
  getTradeCustomFieldDefinitions,
  createTradeCustomField,
  updateTradeCustomField,
  deleteTradeCustomField,
  getTradeColumnPreferences,
  saveTradeColumnPreferences,
  useQuery,
  useAction,
} from "wasp/client/operations";
import type { TradeCustomFieldDefinition, TradeColumnPreference } from "wasp/entities";
import { Settings, Plus, Edit, Trash2, ChevronUp, ChevronDown, X } from "lucide-react";
import { Button } from "../client/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../client/components/ui/dialog";
import { Input } from "../client/components/ui/input";
import { Label } from "../client/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../client/components/ui/select";
import { Checkbox } from "../client/components/ui/checkbox";
import { toast } from "../client/hooks/use-toast";
import useTranslation from "../client/hooks/useTranslation";


interface ColumnConfigDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfigChange?: () => void;
  /** Column keys currently hidden by the widget due to lack of space (visual only, not a preference). */
  spaceHiddenKeys?: string[];
}

interface ColumnItem {
  key: string;
  label: string;
  required: boolean;
  isCustom?: boolean;
  customFieldId?: string;
  isVisible?: boolean;
  isVisibleByDefault?: boolean;
}

export function ColumnConfigDialog({
  open,
  onOpenChange,
  onConfigChange,
  spaceHiddenKeys = [],
}: ColumnConfigDialogProps) {
  const t = useTranslation();
  const spaceHiddenSet = new Set(spaceHiddenKeys);

  const STANDARD_COLUMNS = [
    { key: "date", label: t.journal.date, required: true },
    { key: "exitDate", label: t.journal.exitDate, required: false },
    { key: "symbol", label: t.journal.symbol, required: true },
    { key: "type", label: t.journal.type, required: true },
    { key: "status", label: t.journal.status, required: false },
    { key: "account", label: t.journal.account, required: false },
    { key: "entryPrice", label: t.journal.entryPrice, required: true },
    { key: "exitPrice", label: t.journal.exitPrice, required: false },
    { key: "quantity", label: t.journal.quantity, required: true },
    { key: "pnl", label: t.journal.pnl, required: false },
    { key: "swaps", label: t.journal.swaps, required: false },
    { key: "commission", label: t.journal.commission, required: false },
    { key: "notes", label: t.journal.notes, required: false },
    { key: "strategy", label: t.journal.strategy, required: false },
    { key: "tags", label: t.journal.tags, required: false },
  ];

  const [columns, setColumns] = useState<ColumnItem[]>([]);
  const [isCreatingField, setIsCreatingField] = useState(false);
  const [editingField, setEditingField] = useState<TradeCustomFieldDefinition | null>(null);
  const [fieldToDelete, setFieldToDelete] = useState<string | null>(null);
  const [fieldFormData, setFieldFormData] = useState({
    name: "",
    label: "",
    type: "text" as "text" | "number" | "date" | "boolean" | "tags",
    isRequired: false,
  });

  const { data: customFields = [], refetch: refetchCustomFields } = useQuery(
    getTradeCustomFieldDefinitions,
  );
  const { data: preferences = [], refetch: refetchPreferences } = useQuery(
    getTradeColumnPreferences,
  );

  const createFieldFn = useAction(createTradeCustomField);
  const updateFieldFn = useAction(updateTradeCustomField);
  const deleteFieldFn = useAction(deleteTradeCustomField);
  const savePreferencesFn = useAction(saveTradeColumnPreferences);

  // Construire la liste des colonnes avec leurs préférences
  useEffect(() => {
    if (!open) return;

    const preferenceMap = new Map(
      preferences.map((p: TradeColumnPreference) => [p.columnKey, p]),
    );

    const standardCols: ColumnItem[] = STANDARD_COLUMNS.map((col) => {
      const pref = preferenceMap.get(col.key);
      return {
        ...col,
        isCustom: false,
        isVisible: pref ? pref.isVisible : true, // Par défaut visible, ou selon préférence sauvegardée
        isVisibleByDefault: true,
      };
    });

    const customCols: ColumnItem[] = customFields.map((field) => {
      const pref = preferenceMap.get(`custom:${field.name}`);
      return {
        key: `custom:${field.name}`,
        label: field.label,
        required: field.isRequired,
        isCustom: true,
        customFieldId: field.id,
        isVisible: pref ? pref.isVisible : field.isVisibleByDefault,
        isVisibleByDefault: field.isVisibleByDefault,
      };
    });

    const allColumns = [...standardCols, ...customCols];

    // Trier par ordre des préférences, puis par ordre par défaut
    allColumns.sort((a, b) => {
      const prefA = preferenceMap.get(a.key);
      const prefB = preferenceMap.get(b.key);
      if (prefA && prefB) return prefA.order - prefB.order;
      if (prefA) return -1;
      if (prefB) return 1;
      return 0;
    });

    setColumns(allColumns);
  }, [open, customFields, preferences]);

  const handleToggleColumn = (columnKey: string) => {
    const column = columns.find((c) => c.key === columnKey);
    if (!column || column.required) return;

    setColumns((prev) =>
      prev.map((col) =>
        col.key === columnKey ? { ...col, isVisible: !col.isVisible } : col,
      ),
    );
  };

  const handleMoveColumn = (index: number, direction: "up" | "down") => {
    const newIndex = direction === "up" ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= columns.length) return;

    setColumns((prev) => {
      const newCols = [...prev];
      [newCols[index], newCols[newIndex]] = [newCols[newIndex], newCols[index]];
      return newCols;
    });
  };

  const handleSave = async () => {
    try {
      const preferencesToSave = columns.map((col, index) => ({
        columnKey: col.key,
        isVisible: col.required || col.isVisible !== false,
        order: index,
      }));

      await savePreferencesFn({ preferences: preferencesToSave });
      toast({
        title: t.journal.columnsSaved,
        description: t.journal.columnsSavedDescription,
      });
      refetchPreferences();
      onConfigChange?.();
      onOpenChange(false);
    } catch (error: any) {
      toast({
        title: t.common.error,
        description: error.message || t.journal.cannotSaveColumns,
        variant: "destructive",
      });
    }
  };

  const handleCreateField = async () => {
    try {
      await createFieldFn({
        name: fieldFormData.name,
        label: fieldFormData.label,
        type: fieldFormData.type,
        isRequired: fieldFormData.isRequired,
        order: customFields.length,
        isVisibleByDefault: true,
      });
      toast({
        title: t.journal.fieldCreated,
        description: t.journal.fieldCreatedDescription,
      });
      setFieldFormData({ name: "", label: "", type: "text", isRequired: false });
      setIsCreatingField(false);
      refetchCustomFields();
    } catch (error: any) {
      toast({
        title: t.common.error,
        description: error.message || t.journal.cannotCreateField,
        variant: "destructive",
      });
    }
  };

  const handleUpdateField = async () => {
    if (!editingField) return;

    try {
      await updateFieldFn({
        id: editingField.id,
        label: fieldFormData.label,
        isRequired: fieldFormData.isRequired,
      });
      toast({
        title: t.journal.fieldUpdated,
        description: t.journal.fieldUpdatedDescription,
      });
      setEditingField(null);
      setFieldFormData({ name: "", label: "", type: "text", isRequired: false });
      refetchCustomFields();
    } catch (error: any) {
      toast({
        title: t.common.error,
        description: error.message || t.journal.cannotUpdateField,
        variant: "destructive",
      });
    }
  };

  const handleDeleteField = async (fieldId: string) => {
    try {
      await deleteFieldFn({ id: fieldId });
      toast({
        title: t.journal.fieldDeleted,
        description: t.journal.fieldDeletedDescription,
      });
      setFieldToDelete(null);
      refetchCustomFields();
    } catch (error: any) {
      toast({
        title: t.common.error,
        description: error.message || t.journal.cannotDeleteField,
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t.journal.customizeColumns}</DialogTitle>
          <DialogDescription>
            {t.journal.customizeColumnsDescription}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Liste des colonnes */}
          <div className="space-y-2">
            <Label>{t.journal.columnsLabel}</Label>
            <div className="border rounded-md divide-y">
              {columns.map((column, index) => (
                <div
                  key={column.key}
                  className="flex items-center gap-3 p-3 hover:bg-muted/50"
                >
                  <Checkbox
                    checked={column.required || (column as any).isVisible !== false}
                    disabled={column.required}
                    onCheckedChange={() => handleToggleColumn(column.key)}
                  />
                  <span className="flex-1 text-sm">
                    {column.label}
                    {spaceHiddenSet.has(column.key) &&
                      (column.required || (column as any).isVisible !== false) && (
                        <span className="ml-2 text-[10px] text-muted-foreground">
                          {t.journal.hiddenBySpace}
                        </span>
                      )}
                  </span>
                  {column.isCustom && column.customFieldId && (
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          const field = customFields.find((f) => f.id === column.customFieldId);
                          if (field) {
                            setEditingField(field);
                            setFieldFormData({
                              name: field.name,
                              label: field.label,
                              type: field.type as any,
                              isRequired: field.isRequired,
                            });
                          }
                        }}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        type="button"
                        onClick={() => setFieldToDelete(column.customFieldId!)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleMoveColumn(index, "up")}
                      disabled={index === 0}
                    >
                      <ChevronUp className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleMoveColumn(index, "down")}
                      disabled={index === columns.length - 1}
                    >
                      <ChevronDown className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Bouton pour créer un nouveau champ */}
          {!isCreatingField && !editingField && (
            <Button
              variant="outline"
              onClick={() => setIsCreatingField(true)}
              className="w-full"
            >
              <Plus className="mr-2 h-4 w-4" />
              {t.journal.addCustomField}
            </Button>
          )}

          {/* Formulaire de création/édition de champ */}
          {(isCreatingField || editingField) && (
            <div className="border rounded-md p-4 space-y-4">
              <div className="flex items-center justify-between">
                <Label>
                  {editingField ? t.journal.editField : t.journal.newCustomField}
                </Label>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setIsCreatingField(false);
                    setEditingField(null);
                    setFieldFormData({ name: "", label: "", type: "text", isRequired: false });
                  }}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>

              {!editingField && (
                <div>
                  <Label htmlFor="field-name">{t.journal.fieldTechnicalName}</Label>
                  <Input
                    id="field-name"
                    value={fieldFormData.name}
                    onChange={(e) =>
                      setFieldFormData({ ...fieldFormData, name: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "") })
                    }
                    placeholder={t.journal.fieldNamePlaceholder}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    {t.journal.fieldNameHint}
                  </p>
                </div>
              )}

              <div>
                <Label htmlFor="field-label">{t.journal.fieldDisplayLabel}</Label>
                <Input
                  id="field-label"
                  value={fieldFormData.label}
                  onChange={(e) =>
                    setFieldFormData({ ...fieldFormData, label: e.target.value })
                  }
                  placeholder={t.journal.fieldLabelPlaceholder}
                />
              </div>

              {!editingField && (
                <div>
                  <Label htmlFor="field-type">{t.journal.fieldTypeLabel}</Label>
                  <Select
                    value={fieldFormData.type}
                    onValueChange={(value: any) =>
                      setFieldFormData({ ...fieldFormData, type: value })
                    }
                  >
                    <SelectTrigger id="field-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="text">{t.journal.fieldTypeText}</SelectItem>
                      <SelectItem value="number">{t.journal.fieldTypeNumber}</SelectItem>
                      <SelectItem value="date">{t.journal.fieldTypeDate}</SelectItem>
                      <SelectItem value="boolean">{t.journal.fieldTypeBoolean}</SelectItem>
                      <SelectItem value="tags">{t.journal.fieldTypeTags}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="flex items-center gap-2">
                <Checkbox
                  id="field-required"
                  checked={fieldFormData.isRequired}
                  onCheckedChange={(checked) =>
                    setFieldFormData({ ...fieldFormData, isRequired: checked === true })
                  }
                />
                <Label htmlFor="field-required" className="cursor-pointer">
                  {t.journal.fieldRequired}
                </Label>
              </div>

              <div className="flex gap-2">
                <Button
                  onClick={editingField ? handleUpdateField : handleCreateField}
                  disabled={!fieldFormData.label || (!editingField && !fieldFormData.name)}
                >
                  {editingField ? t.journal.save : t.journal.create}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsCreatingField(false);
                    setEditingField(null);
                    setFieldFormData({ name: "", label: "", type: "text", isRequired: false });
                  }}
                >
                  {t.common.cancel}
                </Button>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t.common.cancel}
          </Button>
          <Button onClick={handleSave}>{t.journal.save}</Button>
        </DialogFooter>
      </DialogContent>

      <Dialog open={!!fieldToDelete} onOpenChange={(o) => { if (!o) setFieldToDelete(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{t.common.delete}</DialogTitle>
            <DialogDescription>{t.journal.deleteFieldConfirm}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setFieldToDelete(null)}>
              {t.common.cancel}
            </Button>
            <Button type="button" variant="destructive" onClick={() => fieldToDelete && handleDeleteField(fieldToDelete)}>
              {t.common.delete}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}

